import { Inject, Injectable, afterNextRender } from '@angular/core';
import { Subject } from 'rxjs';
import { CookieSettings } from '../domain/cookie-settings.model';
import { CookiesSettingsType } from '../domain/cookies-settings-type';
import { ENVIRONMENT, Environment } from '@environment';

const COOKIE_KEY = 'ush-cookies.v2.accepted';

@Injectable({
  providedIn: 'root'
})
export class CookieSettingsService {
  public $cookieSettingsChanged = new Subject<CookieSettings>();
  public $openCookiesConsentDialog = new Subject<CookiesSettingsType[]>();
  private cookieSettings: CookieSettings;

  constructor(@Inject(ENVIRONMENT) private env: Environment) {
    afterNextRender(() => {
      this.evaluateCookieSettings();
    });
  }

  private evaluateCookieSettings(): void {
    const cookieSettingsRepr = localStorage.getItem(COOKIE_KEY);

    let cookieSettingsSaved = false;

    if (cookieSettingsRepr) {
      const cookiesTemp = JSON.parse(cookieSettingsRepr);

      this.cookieSettings = {
        accepted: cookiesTemp.accepted,
        ad_storage: { enabled: cookiesTemp.ad_storage || false },
        analytics_storage: { enabled: cookiesTemp.analytics_storage || false },
        functionality_storage: { enabled: cookiesTemp.functionality_storage || false },
        security_storage: { enabled: cookiesTemp.security_storage || false },
        social_login: { enabled: cookiesTemp.social_login || false }
      };

      cookieSettingsSaved = true;
    } else {
      this.cookieSettings = {
        accepted: false,
        ad_storage: { enabled: false },
        analytics_storage: { enabled: false },
        functionality_storage: { enabled: false },
        security_storage: { enabled: false },
        social_login: { enabled: false }
      };
    }

    if (!this.env.oAuthFacebook && !this.env.oAuthGoogle) {
      this.cookieSettings.social_login.enabled = false;
    }

    if (cookieSettingsSaved) {
      this.$cookieSettingsChanged.next({ ...this.cookieSettings });
    }
  }

  public getCookieSettings(): CookieSettings {
    return this.cookieSettings;
  }

  public saveCookieSettings(cookieSettings: CookieSettings): void {
    this.cookieSettings = { ...cookieSettings };
    const cookieSettingToStore = {
      accepted: cookieSettings.accepted,
      analytics_storage: cookieSettings.analytics_storage.enabled,
      functionality_storage: cookieSettings.functionality_storage.enabled,
      ad_storage: cookieSettings.ad_storage.enabled,
      security_storage: cookieSettings.security_storage.enabled,
      social_login: cookieSettings.security_storage.enabled
    };

    localStorage.setItem(COOKIE_KEY, JSON.stringify(cookieSettingToStore));

    this.$cookieSettingsChanged.next(cookieSettings);
  }

  public getCookieConsentInput(cookiesConsentSettings?: CookiesSettingsType[]): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.$openCookiesConsentDialog.next(cookiesConsentSettings);
      this.$cookieSettingsChanged.subscribe(() => {
        resolve(this.areCookiesConsentEnabled(cookiesConsentSettings));
      });
    });
  }

  public areCookiesConsentEnabled(cookiesConsentSettings: CookiesSettingsType[]) {
    let conditionsMet = true;
    cookiesConsentSettings.forEach(setting => {
      if (setting == 'security_storage' && !this.cookieSettings.security_storage.enabled) conditionsMet = false;
      if (setting == 'analytics_storage' && !this.cookieSettings.analytics_storage.enabled) conditionsMet = false;
      if (setting == 'ad_storage' && !this.cookieSettings.ad_storage.enabled) conditionsMet = false;
      if (setting == 'functionality_storage' && !this.cookieSettings.functionality_storage.enabled)
        conditionsMet = false;
    });
    return conditionsMet;
  }

  public setCookiesSettings(cookiesSettings: CookieSettings) {
    this.cookieSettings = cookiesSettings;
  }
}
